import { GeckoNotificationProvider } from 'features/notifications'
import {
  ActiveTimeRangeSelector,
  ScatterplotProvider,
  ScatterplotSidebar,
  TimeSeriesScatterplotContent,
  useScatterplotContext,
} from 'features/timeSeries'
import { PenDrawer } from 'features/timeSeries/components/PenDrawer'
import { ScatterplotSidebarView } from 'features/timeSeries/components/ScatterplotSidebar'
import { CHART_COLORS } from 'features/timeSeries/config/constants'
import { useExclusivePanels } from 'features/timeSeries/hooks/useExclusivePanels'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`

const ScatterplotAndSidebarsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  min-height: 0;
  align-items: stretch;
  overflow: visible;
  position: relative;
  background: #0a0a0a;
  /* Lets the config sidebar switch to a full-width overlay in tight spaces. */
  container: chartArea / inline-size;
`

const ChartWithFooterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`

const ChartFooter = styled.div`
  padding: 0.25rem 0.375rem;
  border-top: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  background: ${CHART_COLORS.BACKGROUND};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`

interface TimeSeriesScatterplotProps {
  chartID: string
  siteID: string
  showToolbar?: boolean
  showChartSettings?: boolean
  chartAreaRef?: React.RefObject<HTMLDivElement>
  unitID?: string
  unitName?: string
  children?: React.ReactNode
  chartContainer?: React.ComponentType<{ children: React.ReactNode }>
  disableSaving?: boolean
  hideSidebar?: boolean
  onChartSaved?: (chartID: string, isNewChart: boolean) => Promise<void>
  hideHeader?: boolean
  hideFooter?: boolean
  toggleChatbot?: () => void
  /** Initial sensor IDs to load when no chart config exists (e.g., from URL params) */
  initialSensorIds?: string[]
  /** Seed custom line colors per sensor id (applied once per id). */
  initialSensorColors?: Record<string, string>
  /** Sensor IDs rendered de-emphasized (dotted + translucent). */
  deemphasizedSensorIds?: string[]
  /** Initial time range to display; falls back to the last 7 days when omitted */
  initialTimeRange?: { from: string; to: string }
  /** Sensor IDs that should override the loaded chart selection once on mount */
  overrideSensorIds?: string[]
  /** Apply `overrideSensorIds` additively, preserving manually added sensors. */
  preserveManualSensors?: boolean
  /** Time range that should override the loaded chart viewport once on mount */
  overrideTimeRange?: { from: string; to: string }
  /** Manual alert that should be selected when the chart loads */
  initialManualAlertId?: string
  /** Whether saved alerts can be deleted from the alert detail popover. */
  allowSavedAlertDelete?: boolean
  /** Keeps the chart in a loading state while upstream context is resolving. */
  isInitializing?: boolean
  /** Hide the Save, Manual Alert, and Export PNG floating actions. */
  hideFloatingActions?: boolean
  /**
   * Ad-hoc mode: skip resolving `chartID` (no 404 toast) while keeping save
   * functional. First save promotes the transient slug into a persisted chart.
   */
  transient?: boolean
  /** Seed value for the editable chart name. Only meaningful for transient mode. */
  initialChartName?: string
  /**
   * Persist live chart state to localStorage and hydrate it on mount with
   * precedence over the saved chart config. Opt-in; the standalone Process
   * Data pages enable it.
   */
  persistLocalState?: boolean
}

interface TimeSeriesScatterplotInnerProps {
  showToolbar?: boolean
  showChartSettings?: boolean
  chartAreaRef?: React.RefObject<HTMLDivElement>
  unitID?: string
  unitName?: string
  children?: React.ReactNode
  chartContainer?: React.ComponentType<{ children: React.ReactNode }>
  disableSaving?: boolean
  hideSidebar?: boolean
  hideHeader?: boolean
  hideFooter?: boolean
  toggleChatbot?: () => void
  initialManualAlertId?: string
  allowSavedAlertDelete?: boolean
  isInitializing?: boolean
  hideFloatingActions?: boolean
}

const TimeSeriesScatterplotInner: React.FC<
  TimeSeriesScatterplotInnerProps & {
    sidebarView: ScatterplotSidebarView
    setSidebarView: React.Dispatch<React.SetStateAction<ScatterplotSidebarView>>
  }
> = ({
  showChartSettings = true,
  chartAreaRef,
  unitID,
  unitName,
  children,
  chartContainer: ChartContainerComponent,
  sidebarView,
  setSidebarView,
  hideSidebar = false,
  hideFooter = false,
  toggleChatbot,
  initialManualAlertId,
  allowSavedAlertDelete,
  isInitializing = false,
  hideFloatingActions = false,
}) => {
  const {
    sensorEntries,
    selectedSensors,
    timeRange,
    setTimeRange,
    liveTail,
    resetViewport,
  } = useScatterplotContext()
  const {
    leftOpen: scatterplotSidebarOpen,
    rightOpen: penDrawerOpen,
    toggleLeft: handleSettingsClick,
    closeLeft: handleSidebarClose,
    toggleRight: handlePenDrawerClick,
  } = useExclusivePanels(false, true)

  // Force chart resize when sidebar view or pen drawer changes
  useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 100)

    return () => clearTimeout(timer)
  }, [sidebarView, penDrawerOpen])

  return (
    <>
      {children}
      {ChartContainerComponent ? (
        <ChartContainerComponent>
          <MainContainer>
            <ScatterplotAndSidebarsContainer>
              <ChartWithFooterWrapper>
                <TimeSeriesScatterplotContent
                  ref={chartAreaRef}
                  showChartSettings={showChartSettings}
                  unitID={unitID}
                  unitName={unitName}
                  toggleChatbot={toggleChatbot}
                  hideTooltip={penDrawerOpen}
                  initialManualAlertId={initialManualAlertId}
                  allowSavedAlertDelete={allowSavedAlertDelete}
                  isInitializing={isInitializing}
                  hideFloatingActions={hideFloatingActions}
                />
                {!hideFooter && (
                  <ChartFooter>
                    <ActiveTimeRangeSelector
                      timeRange={timeRange}
                      setTimeRange={setTimeRange}
                      liveTail={liveTail}
                      onSettingsClick={handleSettingsClick}
                      onResetViewport={resetViewport}
                      onPenDrawerClick={handlePenDrawerClick}
                      isSettingsOpen={scatterplotSidebarOpen}
                      isPenDrawerOpen={penDrawerOpen}
                    />
                  </ChartFooter>
                )}
              </ChartWithFooterWrapper>
              {!hideSidebar && scatterplotSidebarOpen && (
                <ScatterplotSidebar
                  onClose={handleSidebarClose}
                  tagsOptions={sensorEntries}
                  selectedTags={selectedSensors}
                  setSidebarView={setSidebarView}
                  sidebarView={sidebarView}
                />
              )}
              {penDrawerOpen && <PenDrawer />}
            </ScatterplotAndSidebarsContainer>
          </MainContainer>
        </ChartContainerComponent>
      ) : (
        <MainContainer>
          <TimeSeriesScatterplotContent
            ref={chartAreaRef}
            showChartSettings={showChartSettings}
            unitID={unitID}
            unitName={unitName}
            toggleChatbot={toggleChatbot}
            hideTooltip={penDrawerOpen}
            initialManualAlertId={initialManualAlertId}
            allowSavedAlertDelete={allowSavedAlertDelete}
            isInitializing={isInitializing}
            hideFloatingActions={hideFloatingActions}
          />
        </MainContainer>
      )}
    </>
  )
}

export const TimeSeriesScatterplot: React.FC<TimeSeriesScatterplotProps> = ({
  chartID,
  siteID,
  disableSaving,
  hideSidebar = false,
  onChartSaved,
  hideHeader = false,
  hideFooter = false,
  initialSensorIds,
  initialSensorColors,
  deemphasizedSensorIds,
  initialTimeRange,
  overrideSensorIds,
  preserveManualSensors,
  overrideTimeRange,
  transient,
  initialChartName,
  persistLocalState,
  isInitializing = false,
  ...props
}) => {
  const [sidebarView, setSidebarView] = useState<ScatterplotSidebarView>(
    ScatterplotSidebarView.SETTINGS
  )

  return (
    <GeckoNotificationProvider>
      <ScatterplotProvider
        chartID={chartID}
        siteID={siteID}
        disableSaving={disableSaving}
        onChartSaved={onChartSaved}
        initialSensorIds={initialSensorIds}
        initialSensorColors={initialSensorColors}
        deemphasizedSensorIds={deemphasizedSensorIds}
        initialTimeRange={initialTimeRange}
        overrideSensorIds={overrideSensorIds}
        preserveManualSensors={preserveManualSensors}
        overrideTimeRange={overrideTimeRange}
        transient={transient}
        initialChartName={initialChartName}
        persistLocalState={persistLocalState}>
        <TimeSeriesScatterplotInner
          {...props}
          isInitializing={isInitializing}
          sidebarView={sidebarView}
          setSidebarView={setSidebarView}
          hideSidebar={hideSidebar}
          hideHeader={hideHeader}
          hideFooter={hideFooter}
        />
      </ScatterplotProvider>
    </GeckoNotificationProvider>
  )
}
